import { createTheme } from '@mui/material';
import { rusticCottageTypography, createTypographyStyles } from './components/typographyStyles';
import { createRusticButtonStyles } from './components/buttonStyles';
import { 
  createRusticPaperStyles, 
  createRusticCardStyles, 
  createCardHeaderStyles, 
  createCardContentStyles 
} from './components/paperStyles';
import { 
  createRusticOutlinedInputStyles, 
  createRusticInputLabelStyles, 
  createRusticFormHelperTextStyles 
} from './components/formStyles';

const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#bf6a2d', // Warm amber - like firelight
      light: '#e89d59', // Soft glow
      dark: '#8c4a1a', // Deep ember tones
    },
    secondary: {
      main: '#a77f41', // Aged scotch color
      light: '#d2aa6d', // Lighter whiskey tone
      dark: '#755523', // Deep oak barrel
    },
    background: {
      default: '#f5efe6', // Light cream background
      paper: '#fffaf2',   // Slightly lighter cream for cards
    },
    error: {
      main: '#d32f2f', // Standard error red
    },
    warning: {
      main: '#e65100', // Deep amber for warnings
    },
    success: {
      main: '#558b2f', // Forest green - view from the window
    },
    text: {
      primary: '#3a2d21', // Dark wood brown
      secondary: '#614e3b', // Medium wood brown
    },
    divider: '#d8c9b5', // Light wooden beam color
    action: {
      active: '#bf6a2d',
      hover: 'rgba(191, 106, 45, 0.08)',
      selected: 'rgba(191, 106, 45, 0.16)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
      focus: 'rgba(191, 106, 45, 0.12)',
    },
  },
  typography: rusticCottageTypography,
  shape: {
    borderRadius: 8,
  },
});

// Create the theme with extensive component overrides
const rusticCottageTheme = createTheme({
  ...baseTheme,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f5efe6',
          color: '#3a2d21',
          '&::-webkit-scrollbar': {
            width: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#fffaf2',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#d8c9b5',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a77f41',
          },
        },
        'footer': {
          backgroundColor: '#1f170f !important',
          backgroundImage: 'linear-gradient(#1f170ff2, #1f170ff7) !important',
          color: '#f0e6d9 !important',
          borderTop: '2px solid #a77f41 !important',
          position: 'relative !important',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '5px',
            backgroundImage: 'linear-gradient(90deg, transparent 0%, #a77f4166 50%, transparent 100%)',
          },
          '& .MuiTypography-root': {
            color: '#f0e6d9 !important',
          },
          '& a.MuiLink-root': {
            color: '#e89d59 !important',
          },
          '& a.MuiLink-root:hover': {
            color: '#ffb74d !important',
          },
        },
        '.MuiAppBar-root .MuiSvgIcon-root': {
          color: '#d2aa6d !important', // secondary.light - Lighter whiskey tone
        },
        '.rusticCottage-theme .MuiSvgIcon-root[data-testid="LocalFireDepartmentIcon"]': {
          color: '#bf6a2d !important',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiButton: createRusticButtonStyles(baseTheme),
    MuiPaper: createRusticPaperStyles(baseTheme),
    MuiCard: createRusticCardStyles(baseTheme),
    MuiCardHeader: createCardHeaderStyles(baseTheme),
    MuiCardContent: createCardContentStyles(baseTheme),
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 2px 10px rgba(0,0,0,0.5)',
          backgroundColor: '#1f170f', // Dark background for header
          color: '#f0e6d9', // Light text
          borderBottom: '2px solid #a77f41', // Horizontal divider using secondary.main
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(183, 149, 118, 0.25)',
          color: '#3a2d21',
        },
        head: {
          fontWeight: 600,
          color: '#3a2d21',
          backgroundColor: '#f9f2e7',
        },
        body: {
          color: '#3a2d21',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(183, 149, 118, 0.05)',
          },
          '&:hover': {
            backgroundColor: 'rgba(191, 106, 45, 0.08)',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: '#fffaf2',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: '#fffaf2',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#3a2d21',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: '#3a2d21',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: '#2f251b',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(183, 149, 118, 0.3)',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1f170f', // Dark background for nav
          color: '#f0e6d9', // Light text
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#614e3b',
          '&:hover': {
            backgroundColor: 'rgba(191, 106, 45, 0.15)',
          },
          'footer &': {
            color: '#f0e6d9',
            '&:hover': {
              color: '#ffb74d',
            }
          },
          '.MuiAppBar-root &': {
            color: baseTheme.palette.secondary.light, // Use theme's secondary.light
            '&:hover': {
              color: baseTheme.palette.secondary.main, // Use theme's secondary.main for hover
            },
            '& .MuiAvatar-root': {
              backgroundColor: '#8c4a1a !important', // Dark background in navbar
              color: '#f0e6d9 !important', // Light text for good contrast
            },
          }
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#bf6a2d', // Warm amber for links
          '&:hover': {
            color: '#8c4a1a',
          },
          'footer &': {
            color: '#e89d59', // Brighter links in footer
            '&:hover': {
              color: '#ffb74d',
            }
          }
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(191, 106, 45, 0.15)',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#bf6a2d', // Make icons stand out
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: '#3a2d21', // Dark wood brown
        },
        secondary: {
          color: '#614e3b', // Medium wood brown
        },
      },
    },
    MuiOutlinedInput: createRusticOutlinedInputStyles(baseTheme),
    MuiInputLabel: createRusticInputLabelStyles(baseTheme),
    MuiFormHelperText: createRusticFormHelperTextStyles(baseTheme),
    MuiTypography: createTypographyStyles(baseTheme),
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(183, 149, 118, 0.2)',
          '&.MuiChip-colorDefault': {
            backgroundColor: '#d8c9b5',
            color: '#3a2d21',
            '& .MuiChip-label': {
              color: '#3a2d21',
            },
          },
        },
        filled: {
          backgroundColor: '#614e3b',
          color: '#f0e6d9',
        },
        outlined: {
          borderColor: '#a77f41',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#a77f41',
          '&.Mui-checked': {
            color: '#bf6a2d',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#a77f41',
          '&.Mui-checked': {
            color: '#bf6a2d',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#614e3b',
          color: '#f0e6d9',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#bf6a2d',
            '&:hover': {
              backgroundColor: 'rgba(191, 106, 45, 0.08)',
            },
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#a77f41',
          },
        },
        track: {
          backgroundColor: '#614e3b',
        },
        thumb: {
          backgroundColor: '#d8c9b5',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: '#cf6679', // Use the error color for badges
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: '#d8c9b5',
          '&.Mui-active': {
            color: '#e89d59',
          },
          '&.Mui-completed': {
            color: '#f0e6d9',
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: '#614e3b',
          '&.Mui-active': {
            color: '#bf6a2d',
          },
          '&.Mui-completed': {
            color: '#a77f41',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#3a2d21',
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(97, 78, 59, 0.2)',
          '&.Mui-expanded': {
            minHeight: 48,
          },
        },
        content: {
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          backgroundColor: '#3a2d21',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#614e3b',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: '#a77f41 !important',
          color: '#f0e6d9 !important', // Light text for good contrast on medium brown background
          '&': {
            backgroundColor: '#a77f41', // Ensure background is applied
            color: '#f0e6d9',
          },
        },
        colorDefault: {
          backgroundColor: '#a77f41 !important',
          color: '#f0e6d9 !important',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          backgroundColor: '#3a2d21',
        },
        standardSuccess: {
          backgroundColor: 'rgba(112, 157, 103, 0.15)',
          color: '#f0e6d9',
        },
        standardInfo: {
          backgroundColor: 'rgba(167, 127, 65, 0.15)',
          color: '#f0e6d9',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 183, 77, 0.15)',
          color: '#f0e6d9',
        },
        standardError: {
          backgroundColor: 'rgba(207, 102, 121, 0.15)',
          color: '#f0e6d9',
        },
      },
    },
  },
});

export default rusticCottageTheme; 